import React, { useState } from "react";

import { Table as AntTable } from "antd";
import { isArray, isBoolean, isDate, isNumber, isObject, isString } from "lodash";
import moment from "moment";

import { copyToClipboard, formatNumber, formatNumericCurrency, generateRandomId, gentlyTruncateString, splitByUppercase, stripHtml } from "utils";

import {
  CROPS_SCHEMA,
  FERTILIZERS_APPLICATION_MINERALS_SCHEMA,
  FERTILIZERS_APPLICATION_ORGANICS_SCHEMA,
  FERTILIZERS_APPLICATION_SCHEMA,
  TILLAGE_SEEDING_SCHEMA,
} from "consts";

import { Button, Col, Icon, Input, Link, Modal, Row, TagNormal, Tooltip, TooltipWrapper, Typography, Message as message } from "components";

const generateAction = (action, record, i, setModalConfirm) => {
  if (action.disabled?.(record)) {
    return (
      <Tooltip placement="left" title={action.disabledTooltip ?? action.tooltip} key={i}>
        <Icon iconName={action.icon} className={`${action.className ?? ""} disabled`} />
      </Tooltip>
    );
  }

  if (action.modalConfirm && action.func) {
    return <Icon iconName={action.icon} onClick={() => setModalConfirm({ show: true, action, record })} key={i} />;
  }

  if (action.tooltip && action.func) {
    return (
      <Tooltip placement="left" title={action.tooltip} key={i}>
        <Icon
          iconName={action.icon}
          className={action.className}
          onClick={() => {
            const params = action.arguments ? action.arguments.map((item) => record[item]) : [];
            return params.length ? action.func(...params) : action.func(record);
          }}
        />
      </Tooltip>
    );
  }
  if (action.func) {
    return (
      <Icon
        iconName={action.icon}
        className={action.className}
        key={i}
        onClick={() => {
          const params = action.arguments ? action.arguments.map((item) => record[item]) : [];
          return params.length ? action.func(...params) : action.func(record);
        }}
      />
    );
  }

  if (!action.action && !action.func && action.tooltip) {
    return (
      <Tooltip placement="left" title={action.tooltip} key={i}>
        <Link to={`${action.route}${action.record ? record[action.record] : record.id}`} mixpanelText={action.tooltip}>
          <Icon iconName={action.icon} className={action.className} />
        </Link>
      </Tooltip>
    );
  }
  if (!action.action && !action.func) {
    return (
      <Link to={`${action.route}${action.record ? record[action.record] : record.id}`} key={i} mixpanelText={action.route}>
        <Icon iconName={action.icon} className={action.className} />
      </Link>
    );
  }
};

const addObjectToColum = (name, object, columns) => {
  Object.keys(object).forEach((key) => {
    const columnName = `${key}_${name}`;
    const columnValue = object[key];
    if (isBoolean(columnValue)) {
      columns.push({
        type: "status",
        title: gentlyTruncateString(splitByUppercase(columnName), 15),
        dataIndex: columnName,
        key: columnName,
        filters: [
          {
            text: "Yup",
            value: true,
          },
          {
            text: "Nope",
            value: false,
          },
        ],
        filterMultiple: false,
        onFilter: (value, record) => record?.[name]?.[key] === value,
        render: (text, record) => {
          const value = record?.[name]?.[key];
          return <>{value ? <TagNormal color="green" text="Yup" /> : value === undefined ? null : <TagNormal color="red" text="Nope" />}</>;
        },
      });
      return;
    }
    if (isString(columnValue)) {
      columns.push({
        type: "string",
        title: gentlyTruncateString(splitByUppercase(columnName), 15),
        dataIndex: columnName,
        key: columnName,
        searchProp: columnName,
        filterSearch: true,
        render: (text, record) => {
          const value = record?.[name]?.[key];
          return value ? gentlyTruncateString(stripHtml(value), 150) : "";
        },
      });
      return;
    }
    if (isNumber(columnValue)) {
      columns.push({
        type: "number",
        title: gentlyTruncateString(splitByUppercase(columnName), 15),
        dataIndex: columnName,
        key: columnName,
        sorter: (a, b) => a[columnName] - b[columnName],
        render: (text, record) => {
          const value = record?.[name]?.[key];
          return columnName === "payment" || columnName === "funds"
            ? formatNumericCurrency(value)
            : parseFloat(value) % 1 > 0
            ? formatNumber({ number: parseFloat(value) })
            : value;
        },
      });
      return;
    }
    if (isArray(columnValue)) {
      columns.push({
        type: "tags",
        title: gentlyTruncateString(splitByUppercase(columnName), 15),
        dataIndex: columnName,
        key: columnName,
        searchProp: key,
        render: (text, record) => (
          <>
            {record?.[name]?.[key]?.map((item, i) => (
              <TagNormal key={i} text={item} />
            ))}
          </>
        ),
      });
    }
  });
};

const generateColumnsByModuleSchema = (data, config, setModalConfirm) => {
  const columns = [];
  let orderedColumns = [];
  const dateFormats = ["DD.MM.YYYY", "D.M.YYYY", "DD.M.YYYY", "D.MM.YYYY", "YYYY-MM-DD HH:mm:ss.SSS"];
  const exclude = config && config.exclude ? config.exclude : [];
  const actions = config && config.actions ? config.actions : [];

  Object.keys(data).forEach((key) => {
    const item = data[key];
    if (exclude.includes(key)) {
      return;
    }
    if (isDate(item)) {
      columns.push({
        type: "date",
        title: gentlyTruncateString(splitByUppercase(key), 16),
        dataIndex: key,
        key: key,
        defaultSortOrder: "descend",
        sorter: (a, b) => {
          const first = moment(`${a[key]}`.replace("T", " ").replace("Z", ""), dateFormats);
          const second = moment(`${b[key]}`.replace("T", " ").replace("Z", ""), dateFormats);
          return first.diff(second);
        },
        render: (text) => {
          if (moment(text).isSame(moment(0))) {
            return "";
          }
          return moment(`${text}`.replace("T", " ").replace("Z", ""), ["DD.MM.YYYY", "D.M.YYYY", "DD.M.YYYY", "D.MM.YYYY", "YYYY-MM-DD HH:mm:ss.SSS"]).format(
            "DD.MM.YYYY"
          );
        },
      });
      return;
    }
    if (isBoolean(item)) {
      columns.push({
        type: "status",
        title: gentlyTruncateString(splitByUppercase(key), 16),
        dataIndex: key,
        key: key,
        filters: [
          {
            text: "Yup",
            value: true,
          },
          {
            text: "Nope",
            value: false,
          },
        ],
        filterMultiple: false,
        onFilter: (value, record) => record[key] === value,
        render: (text, record) => <>{record[key] ? <TagNormal color="green" text="Yup" /> : <TagNormal color="red" text="Nope" />}</>,
      });
      return;
    }
    if (isArray(item)) {
      columns.push({
        type: "tags",
        title: gentlyTruncateString(splitByUppercase(key), 16),
        dataIndex: key,
        key: key,
        searchProp: key,
        render: (items) => (
          <>
            {items?.map((item, i) =>
              typeof item === "string" ? <TagNormal key={i} text={item} /> : <TagNormal key={i} text={item[Object.keys(item)[0]]?.toUpperCase()} />
            )}
          </>
        ),
      });
      return;
    }
    if (key.trim().toLowerCase().endsWith("id")) {
      columns.push({
        type: "id",
        title: gentlyTruncateString(splitByUppercase(key), 16),
        dataIndex: key,
        key: key,
        sorter: (a, b) => {
          if (a[key] < b[key]) {
            return -1;
          }
          if (a[key] > b[key]) {
            return 1;
          }
          return 0;
        },
        searchProp: key,
        width: "115px",
        render: (text, record) => (
          <Typography variant="paragraph-m" onClick={() => copyToClipboard(record[key])} className="pointer">
            <b>
              <small>
                {record[key].split("-")[0]}... <Icon iconName="tabler/copy" className="green-text pointer inline" size="s" />
              </small>
            </b>
          </Typography>
        ),
      });
      return;
    }
    if (isString(item)) {
      columns.push({
        type: "string",
        title: gentlyTruncateString(splitByUppercase(key), 16),
        dataIndex: key,
        key: key,
        searchProp: key,
        filterSearch: true,
        render: (text, record) => (record[key] ? gentlyTruncateString(stripHtml(record[key]), 150) : ""), // Error
      });
      return;
    }
    if (isNumber(item)) {
      columns.push({
        type: "number",
        title: gentlyTruncateString(splitByUppercase(key), 16),
        dataIndex: key,
        key: key,
        sorter: (a, b) => a[key] - b[key],
        render: (text, record) =>
          key === "payment" || key === "funds"
            ? formatNumericCurrency(record[key])
            : parseFloat(record[key]) % 1 > 0
            ? formatNumber({ number: parseFloat(record[key]) })
            : record[key],
      });
      return;
    }
    if (isObject(item)) {
      addObjectToColum(key, item, columns);
    }
    if (actions.length) {
      columns.push({
        type: "action",
        title: "",
        key: "action",
        width: actions.length * 40,
        render: (text, record) => actions.map((action, i) => generateAction(action, record, i, setModalConfirm)),
      });
    }
  });

  // order columns so we have a consistent layout
  // eslint-disable-next-line
  orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "id")];
  // eslint-disable-next-line
  orderedColumns = [
    ...orderedColumns,
    ...columns.filter((col) => col.type !== "id" && col.type !== "date" && col.type !== "action" && col.type !== "date" && col.type !== "status"),
  ];
  // eslint-disable-next-line
  orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "status")];
  // eslint-disable-next-line
  orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "date")];
  // eslint-disable-next-line
  orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "action")];

  return orderedColumns;
};

const FilterPopup = ({ selectedKeys, setSelectedKeys, setSearch, handleSearch, dataIndex, handleReset, clearFilters, confirm }) => (
  <>
    <Row>
      <Col>
        <Input
          label={"Search..."}
          initialValue={selectedKeys[0]}
          onChange={(e) => setSearch(setSelectedKeys, e.target.value)}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          value={selectedKeys[0]}
        />
      </Col>
    </Row>
    <Row>
      <Col sm={12}>
        <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} size="small">
          Search
        </Button>
      </Col>
      <Col sm={12}>
        <Button onClick={() => handleReset(clearFilters, dataIndex)} size="small">
          Reset
        </Button>
      </Col>
    </Row>
  </>
);

const AppliedFilters = ({ appliedFilters }) => (
  <div className="margin-bottom-50">
    {appliedFilters.map((filter, index) => (
      <TagNormal key={index} index={index} type={"main-filter"} text={`${filter.name}: "${filter.value}"`} />
    ))}
  </div>
);

export const AdminTable = ({ dataSource, width, config, title, type, className, onRowSelectionChange, rowKey, showSelectedFilters, loading }) => {
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [password, setPassword] = useState(null);
  const [modalConfirm, setModalConfirm] = useState(false);

  const addAppliedFilter = (item) => {
    appliedFilters.push(item);
    setAppliedFilters([...appliedFilters]);
  };
  const updateAppliedFilter = (index, item, value) => {
    appliedFilters[index] = { ...item, value: value };
    setAppliedFilters([...appliedFilters]);
  };
  const removeAppliedFilter = (index) => {
    appliedFilters.splice(index, 1);
    setAppliedFilters([...appliedFilters]);
  };

  const updateAppliedFilters = (columnName, value) => {
    const item = { name: columnName, value };
    let currentFilterIndex = null;
    const filterExists = appliedFilters.find((filter, index) => {
      currentFilterIndex = index;
      return filter.name === columnName;
    });

    if (filterExists) {
      if (value) {
        updateAppliedFilter(currentFilterIndex, item, value);
        return;
      }
      removeAppliedFilter(currentFilterIndex);
    } else {
      if (value) {
        addAppliedFilter(item);
        return;
      }
      removeAppliedFilter(currentFilterIndex);
    }
  };

  const restAppliedFilter = (columnName) => {
    let filterIndex = null;
    appliedFilters.find((filter, index) => {
      if (filter.name !== columnName) return undefined;
      filterIndex = index;
      return true;
    });

    if (filterIndex !== null) {
      removeAppliedFilter(filterIndex);
    }
  };

  const setSearch = (setSelectedKeys, v) => {
    setSelectedKeys(v ? [v] : []);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    updateAppliedFilters(dataIndex, selectedKeys[0]);
  };

  const handleReset = (clearFilters, columnName) => {
    clearFilters({ confirm: true });
    restAppliedFilter(columnName);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <FilterPopup
        selectedKeys={selectedKeys}
        dataIndex={dataIndex}
        confirm={confirm}
        setSelectedKeys={setSelectedKeys}
        setSearch={setSearch}
        handleSearch={handleSearch}
        handleReset={handleReset}
        clearFilters={clearFilters}
      />
    ),
    filterIcon: () => <Icon iconName="tabler/search" size="s" className="icon inline" />,
    onFilter: (value, record) => {
      if (record[dataIndex] && Array.isArray(record[dataIndex])) {
        let result = false;
        record[dataIndex].forEach((item) => {
          for (const k in item) {
            if (item[k].toLowerCase().includes(value.toLowerCase())) {
              result = true;
            }
          }
        });

        return result;
      }
      if (isNumber(record[dataIndex])) {
        return record[dataIndex].toString() === value;
      }
      if (record[dataIndex]) {
        return record[dataIndex].toString().toLowerCase().includes(value.toLowerCase());
      }

      return false;
    },
    render: (text) => text,
  });

  const generateColumns = (data) => {
    const columns = [];
    let orderedColumns = [];
    const keys = Object.keys(data[0]);
    const exclude = config && config.exclude ? config.exclude : [];
    const actions = config && config.actions ? config.actions : [];

    const dateFormats = ["DD.MM.YYYY", "D.M.YYYY", "DD.M.YYYY", "D.MM.YYYY", "YYYY-MM-DD HH:mm:ss.SSS"];
    // create the columns based on data type
    for (const key of keys) {
      if (!exclude.includes(key)) {
        let columnType = null;
        if (moment(`${data[0][key]}`.replace("T", " ").replace("Z", ""), dateFormats, true).isValid()) {
          columnType = "date";
        } else if (data.some((element) => typeof element[key] === "boolean")) {
          columnType = "status";
        } else if (data.some((element) => Array.isArray(element[key]))) {
          columnType = "tags";
        } else if (key.trim().toLowerCase().endsWith("id")) {
          columnType = "id";
          // eslint-disable-next-line
        } else if (data.some((element) => isNaN(element[key]))) {
          columnType = "string";
        } else {
          columnType = "number";
        }

        switch (columnType) {
          case "date":
            columns.push({
              type: "date",
              title: gentlyTruncateString(splitByUppercase(key), 16),
              dataIndex: key,
              key: key,
              defaultSortOrder: "descend",
              sorter: (a, b) => {
                const first = moment(`${a[key]}`.replace("T", " ").replace("Z", ""), dateFormats);
                const second = moment(`${b[key]}`.replace("T", " ").replace("Z", ""), dateFormats);
                return first.diff(second);
              },
              render: (text) => {
                if (moment(text).isSame(moment(0))) {
                  return "";
                }
                return moment(`${text}`.replace("T", " ").replace("Z", ""), [
                  "DD.MM.YYYY",
                  "D.M.YYYY",
                  "DD.M.YYYY",
                  "D.MM.YYYY",
                  "YYYY-MM-DD HH:mm:ss.SSS",
                ]).format("DD.MM.YYYY");
              },
            });
            break;
          case "status":
            columns.push({
              type: "status",
              title: gentlyTruncateString(splitByUppercase(key), 16),
              dataIndex: key,
              key: key,
              filters: [
                {
                  text: "Yup",
                  value: true,
                },
                {
                  text: "Nope",
                  value: false,
                },
              ],
              filterMultiple: false,
              onFilter: (value, record) => record[key] === value,
              render: (text, record) => <>{record[key] ? <TagNormal color="green" text="Yup" /> : <TagNormal color="red" text="Nope" />}</>,
            });
            break;
          case "tags":
            columns.push({
              type: "tags",
              title: gentlyTruncateString(splitByUppercase(key), 16),
              dataIndex: key,
              key: key,
              searchProp: key,
              render: (items) => (
                <>
                  {items?.map((item, i) =>
                    typeof item === "string" ? <TagNormal key={i} text={item} /> : <TagNormal key={i} text={item[Object.keys(item)[0]]?.toUpperCase()} />
                  )}
                </>
              ),
            });
            break;
          case "id":
            columns.push({
              type: "id",
              title: gentlyTruncateString(splitByUppercase(key), 16),
              dataIndex: key,
              key: key,
              sorter: (a, b) => {
                if (a[key] < b[key]) {
                  return -1;
                }
                if (a[key] > b[key]) {
                  return 1;
                }
                return 0;
              },
              searchProp: key,
              width: "115px",
              render: (text, record) => (
                <TooltipWrapper tooltip={{ title: record[key], placement: "top" }}>
                  <Typography variant="paragraph-m" onClick={() => copyToClipboard(record[key])} className="pointer">
                    <b>
                      <small>
                        {record[key].split("-")[0]}... <Icon iconName="tabler/copy" className="green-text pointer inline" size="s" />
                      </small>
                    </b>
                  </Typography>
                </TooltipWrapper>
              ),
            });
            break;
          case "string":
            columns.push({
              type: "string",
              title: gentlyTruncateString(splitByUppercase(key), 16),
              dataIndex: key,
              key: key,
              searchProp: key,
              filterSearch: true,
              render: (text, record) => (
                <TooltipWrapper tooltip={{ title: stripHtml(record[key]), placement: "top" }}>
                  {record[key] ? gentlyTruncateString(stripHtml(record[key]), 50) : ""}
                </TooltipWrapper>
              ),
            });
            break;
          case "number":
            columns.push({
              type: "number",
              title: gentlyTruncateString(splitByUppercase(key), 16),
              dataIndex: key,
              key: key,
              searchProp: key,
              filterSearch: true,
              sorter: (a, b) => a[key] - b[key],
              render: (text, record) =>
                key === "payment" || key === "funds"
                  ? formatNumericCurrency(record[key])
                  : parseFloat(Math.abs(record[key])) % 1 > 0
                  ? formatNumber({ number: parseFloat(record[key]) })
                  : record[key],
            });
            break;
          default:
            break;
        }
      }
    }
    if (actions.length) {
      columns.push({
        type: "action",
        title: "",
        key: "action",
        className: "action",
        width: actions.length * 40,
        render: (text, record) => actions.map((action, i) => generateAction(action, record, i)),
      });
    }

    // order columns so we have a consistent layout
    orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "id")];
    orderedColumns = [
      ...orderedColumns,
      ...columns.filter((col) => col.type !== "id" && col.type !== "date" && col.type !== "action" && col.type !== "date" && col.type !== "status"),
    ];
    orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "status")];
    orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "date")];
    orderedColumns = [...orderedColumns, ...columns.filter((col) => col.type === "action")];

    return orderedColumns;
  };

  const getColumnsByType = (type) => {
    if (type === "crops") {
      return generateColumnsByModuleSchema(CROPS_SCHEMA, config, setModalConfirm);
    }
    if (type === "tillage" || type === "seeding") {
      return generateColumnsByModuleSchema(TILLAGE_SEEDING_SCHEMA, config, setModalConfirm);
    }
    if (type === "applications") {
      return generateColumnsByModuleSchema(FERTILIZERS_APPLICATION_SCHEMA, config, setModalConfirm);
    }
    if (type === "mineral") {
      return generateColumnsByModuleSchema(FERTILIZERS_APPLICATION_MINERALS_SCHEMA, config, setModalConfirm);
    }
    if (type === "organic") {
      return generateColumnsByModuleSchema(FERTILIZERS_APPLICATION_ORGANICS_SCHEMA, config, setModalConfirm);
    }
    return generateColumns(dataSource);
  };

  const columnData =
    dataSource && dataSource.length > 0
      ? getColumnsByType(type).map((column) => (column.searchProp ? { ...getColumnSearchProps(column.searchProp), ...column } : { ...column }))
      : null;

  return dataSource && dataSource.length > 0 ? (
    <>
      {showSelectedFilters && <AppliedFilters appliedFilters={appliedFilters} handleReset={handleReset} />}
      <AntTable
        rowSelection={
          onRowSelectionChange
            ? {
                type: "checkbox",
                onChange: onRowSelectionChange,
              }
            : null
        }
        title={title}
        rowKey={rowKey || generateRandomId}
        showSorterTooltip={false}
        columns={columnData}
        dataSource={dataSource}
        width={width}
        pagination={{
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          defaultPageSize: config?.defaultPageSize || 100,
        }}
        className={className}
        loading={loading}
      />
      {modalConfirm && modalConfirm.show && (
        <Modal
          input={{
            initialValue: "",
            label: "Secret key",
            onChange: (e) => setPassword(e.target.value),
          }}
          title={modalConfirm.action.modalConfirm.title}
          okText="Confirm"
          cancelText="Cancel"
          onOk={() => {
            if (password === modalConfirm.action.modalConfirm.password) {
              setModalConfirm(false);
              const params = modalConfirm.action.arguments ? modalConfirm.action.arguments.map((item) => modalConfirm.record[item]) : [];
              return params.length ? modalConfirm.action.func(...params) : modalConfirm.action.func(modalConfirm.record);
            }
            message({
              content: "The gremlins did not accept your secret key.",
              icon: "gremlin",
            });
            setModalConfirm(false);
            setPassword(null);
          }}
          showModal={modalConfirm.show}
          onCancel={() => setModalConfirm(false)}
        />
      )}
    </>
  ) : (
    <></>
  );
};

export default AdminTable;
